import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import theme from "../../theme";
import { Avatar, Box, Fab, Typography } from "@mui/material";
import { useDispatch } from "../../store";
import { handleToastState } from "../../slices/shared";
import { Close, CloudUpload } from "@mui/icons-material";

export interface Props {
  onDrop: (acceptedFiles: File[]) => void;
}

const baseStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px",
  borderRadius: "5px",
  borderColor: theme.palette.background.default,
  borderStyle: "dashed",
  color: theme.palette.primary.main,
  outline: "none",
  transition: "border .24s ease-in-out",
  cursor: "pointer",
};

const activeStyle = {
  borderColor: theme.palette.primary.main,
};

const acceptStyle = {
  borderColor: theme.palette.success.main,
};

const rejectStyle = {
  borderColor: theme.palette.error.main,
};

const Dropzone: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const [files, setFiles] = useState<any[]>([]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".xlsx", ".pdf", ".docx", ".doc"],
    },
    maxSize: 524288000,
    multiple: false,
    maxFiles: 1,
    onDrop: async (acceptedFiles, fileRejections) => {
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === "file-too-large") {
            dispatch(
              handleToastState({
                open: "error",
                message:
                  "Error: File is too large! Please resize and try again.",
              })
            );
          }
          if (err.code === "file-invalid-type") {
            dispatch(
              handleToastState({
                open: "error",
                message:
                  "Error: File type not accepted! Please convert to another file type and try again.",
              })
            );
          }
        });
      });

      setFiles(
        acceptedFiles?.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );

      await props.onDrop(acceptedFiles);
    },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const previews = files.map((file) => (
    <Box
      key={file.name}
      display="flex"
      flexDirection="column"
      borderRadius={"5px"}
      border={`2px solid ${theme.palette.background.default}`}
      alignContent="center"
      width={100}
      height={100}
      boxSizing="border-box"
    >
      <Box
        position="absolute"
        width="100%"
        zIndex={5000}
        color="#fff"
        sx={{ cursor: "pointer" }}
        display="flex"
        flexDirection="row"
      >
        <Fab size="small" color="primary">
          <Close onClick={() => setFiles([])} />
        </Fab>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        minWidth={0}
        width="100%"
        overflow="hidden"
        position="relative"
      >
        <Avatar
          sx={{
            backgroundColor: "transparent",
            width: 100,
            height: 100,
            color: "#fff",
          }}
        >
          <Box
            component="img"
            sx={{
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              width: "100%",
              objectFit: "scale-down",
            }}
            src={file.preview}
            alt="img preview"
            onLoad={() => {
              URL.revokeObjectURL(file.preview);
            }}
          />
        </Avatar>
        {/* <Box
          component="img"
          sx={{
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            width: "100%",
            objectFit: "scale-down",
          }}
          src={file.preview}
          alt="img preview"
          onLoad={() => {
            URL.revokeObjectURL(file.preview);
          }}
        /> */}
      </Box>
    </Box>
  ));

  return (
    <>
      <Box
        className="container"
        display="flex"
        flexDirection="column"
        sx={{ marginBottom: 2 }}
        gap={1}
      >
        <Box
          {...getRootProps({ style })}
          display="flex"
          flexDirection="row"
          gap={2}
        >
          <input {...getInputProps()} />
          <Box
            display="flex"
            flexDirection="row"
            sx={{ marginBottom: 1, fontSize: "50px" }}
          >
            <CloudUpload sx={{ cursor: "pointer" }} fontSize="inherit" />
          </Box>
          <Typography variant="body1">
            Drag and drop to upload a file here, or click to select
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          justifyContent="center"
        >
          {previews}
        </Box>
      </Box>
    </>
  );
};

export default Dropzone;
